import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../services/api";
import useAuthStore from "../context/authStore";

// no-op logger in production
const devLog = (...args) => {
  if (import.meta.env.MODE !== "production") {
    console.log(...args);
  }
};

/**
 * Hook to fetch and cache client organization services
 * Returns: { services, loading, error, refetch }
 * Services structure: { consulting, staffing, lms }
 */
export default function useClientServices() {
  const { token } = useAuthStore();
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServices = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiGet("/organizations/me");
      devLog("useClientServices - Raw API response:", data);
      devLog("useClientServices - Organization:", data?.organization);
      const orgServices = data?.organization?.services || {
        consulting: { enabled: false },
        staffing: { enabled: false },
        lms: { enabled: false },
      };
      devLog("useClientServices - Extracted services:", orgServices);
      setServices(orgServices);
    } catch (err) {
      console.error("Failed to fetch client services:", err);
      // Default to no services if fetch fails
      setServices({
        consulting: { enabled: false },
        staffing: { enabled: false },
        lms: { enabled: false },
      });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchServices();

    // No polling - services are cached for the session
    // User can refresh browser to pick up admin changes
  }, [fetchServices]);

  return { services, loading, error, refetch: fetchServices };
}
